/**
 * Defines the typed route tree for the backoffice.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
} from "@tanstack/react-router";

import { SystemStatusPage } from "../modules/system/pages/SystemStatusPage";
import { BackofficeWorkspacePage } from "../modules/workspace/pages/BackofficeWorkspacePage";
import {
  ArtifactsRoutePage,
  BookNoticeRoutePage,
  CasesRoutePage,
  EscalationsRoutePage,
  FollowUpsRoutePage,
  HomeRoutePage,
  NotificationsRoutePage,
  PlansRoutePage,
  PlatformRoutePage,
  CaseReasonsRoutePage,
  CompanyProfileRoutePage,
  TagsRoutePage,
  UsersRoutePage,
} from "../modules/workspace/WorkspaceRoutes";

function ApplicationLayout() {
  return <Outlet />;
}

const rootRoute = createRootRoute({
  component: ApplicationLayout,
});

const workspaceLayoutRoute = createRoute({
  component: BackofficeWorkspacePage,
  getParentRoute: () => rootRoute,
  id: "workspace",
});

const homeRoute = createRoute({
  component: HomeRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/",
});

const casesRoute = createRoute({
  component: CasesRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/cases",
});

const caseDetailRoute = createRoute({
  component: CasesRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/cases/$publicId",
});

const followUpsRoute = createRoute({
  component: FollowUpsRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/follow-ups",
});

const notificationsRoute = createRoute({
  component: NotificationsRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/notifications",
});

const escalationsRoute = createRoute({
  component: EscalationsRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/escalations",
});

const bookNoticeRoute = createRoute({
  component: BookNoticeRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/book-notice",
});

const caseReasonsRoute = createRoute({
  component: CaseReasonsRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/case-reasons",
});

const tagsRoute = createRoute({
  component: TagsRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/tags",
});

const usersRoute = createRoute({
  component: UsersRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/users",
});

const artifactsRoute = createRoute({
  component: ArtifactsRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/artifacts",
});

const companyProfileRoute = createRoute({
  component: CompanyProfileRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/company",
});

const platformCompaniesRoute = createRoute({
  component: PlatformRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/platform/companies",
});

const platformPlansRoute = createRoute({
  component: PlansRoutePage,
  getParentRoute: () => workspaceLayoutRoute,
  path: "/platform/plans",
});

const systemRoute = createRoute({
  component: SystemStatusPage,
  getParentRoute: () => rootRoute,
  path: "/system",
});

const routeTree = rootRoute.addChildren([
  workspaceLayoutRoute.addChildren([
    homeRoute,
    casesRoute,
    caseDetailRoute,
    followUpsRoute,
    notificationsRoute,
    tagsRoute,
    caseReasonsRoute,
    bookNoticeRoute,
    usersRoute,
    escalationsRoute,
    artifactsRoute,
    companyProfileRoute,
    platformCompaniesRoute,
    platformPlansRoute,
  ]),
  systemRoute,
]);

const router = createRouter({
  defaultPreload: "intent",
  routeTree,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Provides the application router.
 */
export function AppRouter() {
  return <RouterProvider router={router} />;
}
